import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Building, Palette, Map, ClipboardList } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  building: Building,
  palette: Palette,
  map: Map,
  clipboard: ClipboardList,
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const processSteps = [
    {
      step: "01",
      title: "Discovery",
      description: "We begin by understanding your vision, needs, and the unique context of your project through in-depth consultations.",
    },
    {
      step: "02",
      title: "Concept",
      description: "Our team develops initial design concepts that balance aesthetics, functionality, and sustainability.",
    },
    {
      step: "03",
      title: "Development",
      description: "We refine the chosen concept, developing detailed designs, specifications, and documentation.",
    },
    {
      step: "04",
      title: "Realization",
      description: "We oversee construction to ensure your vision is realized with the highest quality standards.",
    },
  ];

  return (
    <div data-testid="services-page" className="bg-[#F4F4F0] pt-24">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            Our Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            Comprehensive
            <br />
            design solutions.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed max-w-2xl"
          >
            From initial concept to final construction, we offer end-to-end architectural services tailored to your unique requirements.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section
        data-testid="services-grid-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#E5E5E0]"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Building;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F4F4F0] p-8 md:p-12 border border-[#1A1A1A]/10 hover:border-[#1A1A1A] transition-colors duration-500"
                data-testid={`service-detail-${index}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center border border-[#3A4D39]">
                    <IconComponent size={24} className="text-[#3A4D39]" />
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/40">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#1A1A1A]/60 leading-relaxed mb-8">
                  {service.description}
                </p>
                {service.features && service.features.length > 0 && (
                  <ul className="space-y-3">
                    {service.features.map((feature, fIndex) => (
                      <li
                        key={fIndex}
                        className="flex items-center gap-3 text-sm text-[#1A1A1A]/70"
                      >
                        <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section
        data-testid="process-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
      >
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-4">
            Our Process
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-6">
            From vision to reality
          </h2>
          <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
            Our proven four-stage process ensures your project is delivered with precision and care.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {processSteps.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative"
              data-testid={`process-step-${index}`}
            >
              <span className="font-heading text-7xl md:text-8xl text-[#3A4D39]/10">
                {item.step}
              </span>
              <div className="mt-[-2rem]">
                <h3 className="font-heading text-2xl text-[#1A1A1A] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Approach Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A1A1A] text-[#F4F4F0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/50 mb-6">
              Our Approach
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light mb-8">
              Designing for
              <br />
              generations.
            </h2>
            <p className="text-lg font-light text-[#F4F4F0]/70 leading-relaxed mb-6">
              We believe in creating architecture that stands the test of time. Our designs are thoughtful, sustainable, and deeply connected to their context.
            </p>
            <p className="text-lg font-light text-[#F4F4F0]/70 leading-relaxed">
              Every project is an opportunity to create something meaningful—spaces that enhance lives and contribute positively to their surroundings.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1610650394144-a778795cf585?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjBza2V0Y2hpbmclMjBvbiUyMGJsdWVwcmludCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcyMjYwNDgyfDA&ixlib=rb-4.1.0&q=85"
              alt="Architect working on blueprints"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-6">
            Let's build something extraordinary
          </h2>
          <p className="text-lg font-light text-[#1A1A1A]/70 mb-10">
            Ready to transform your vision into reality? Contact us to discuss your project.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#3A4D39] transition-colors duration-300"
          >
            Start Your Project
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
